import { ActivatedRoute, Params, Router } from '@angular/router';
import { BlogArticle, BlogArticleAdapter } from '@shared/models/blog-article.model';
import { BlogCategory } from '@shared/models/blog-category.model';
import { BlogPage, BlogPageAdapter } from '@shared/models/blog-page.model';
import { BlogService, TrackingService } from '@core/services';
import { Component, Inject, OnDestroy, OnInit, DOCUMENT } from '@angular/core';
import { environment } from '@env/environment';
import { take } from 'rxjs/operators';
import { GhGlobal } from '@shared/helpers/global-functions';
import { ISocialShare } from '@core/seo/social-share/social-share.interface';
import { Location } from '@angular/common';
import { JsonLdService } from '@core/seo/json-ld/json-ld.service';
import { SocialShareService } from '@core/seo/social-share/social-share.service';

@Component({
    selector: 'gh-category',
    templateUrl: 'category.component.html',
    standalone: false
})

export class CategoryComponent implements OnInit, OnDestroy {
  category: BlogCategory;
  altCategory: BlogCategory;
  articlesInCategory: BlogArticle[];
  pager: BlogPage;
  altPager: BlogPage;
  currentPage: any = 1;
  pageIndex: number = 0;
  totalPages: number;
  altTotalPages: number;


  private parametersObservable: any;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private blogArticleAdapter: BlogArticleAdapter,
    private blogPageAdapter: BlogPageAdapter,
    private blogService: BlogService,
    private jsonLdService: JsonLdService,
    private socialShareService: SocialShareService,
    private trackingService: TrackingService,
    protected route: ActivatedRoute,
    protected location: Location,
    protected router: Router,
  ) { }

  ngOnInit() {
    this.parametersObservable = this.route.params
      .subscribe((params) => {
        // "category" is obtained from 'CategoryResolver'
        this.category = this.route.snapshot.data['category'];
        if (!this.category || !this.category.slug) {
          this.notFound();
          return;
        }
        GhGlobal.injectStyle(this.category.color, this.document);

        this.currentPage = +params.page ? +params.page : 1;
        this.pageIndex = this.currentPage - 1;
        this.getArticlesForCategory(this.category.id, this.pageIndex.toString());
        this.getAltCategoryId();
      });
  }

  gotoPage(ev: any) {
    this.getArticlesForCategory(this.category.id, ev.pageIndex);
  }

  ngOnDestroy() {
    this.parametersObservable?.unsubscribe();
  }

  private getArticlesForCategory(categoryId: number, pageNumber: string) {
    this.blogService.getArticlesForCategory(categoryId, pageNumber)
      .pipe(take(1))
      .subscribe((res) => {
        if (res && !res.error) {
          this.pager = this.blogPageAdapter.adapt(res);
          const articles: BlogArticle[] = res.content || [];
          this.articlesInCategory = [];
          this.totalPages = this.pager.totalPages;

          if (this.currentPage > this.totalPages) {
            this.notFound();
            return;
          }

          articles.forEach((article) => {
            this.articlesInCategory.push(this.blogArticleAdapter.adapt(article));
          });
        }
      });
  }

  private getAltCategoryId() {
    const locale = environment.locale === 'en-us' ? 'en-gb' : 'en-us';

    this.blogService.getCategoryBySlug(this.category.slug, locale)
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.altCategory = res;
          this.getAltTotalPages(locale);
        } else {
          this.setSeoData(this.category);
        }
      });
  }

  private getAltTotalPages(locale: string) {
    this.blogService.getArticlesForCategory(this.altCategory.id, '0', locale)
      .pipe(take(1))
      .subscribe((res) => {
        if (res && !res.error) {
          this.altPager = this.blogPageAdapter.adapt(res);
          this.altTotalPages = this.altPager.totalPages;
        }
        this.setSeoData(this.category);
      });
  }

  private notFound() {
    this.router.navigate(['/404']);
  }

  private setSeoData(category: BlogCategory) {
    const seoData: ISocialShare = {
      title: `${category.name} category | GoHenry`,
      keywords: `gohenry, blog, ${category.name}, category`,
      description: category.description,
      image: category.marker,
      url: environment.location + this.location.path(),
      type: 'article.category',
      author: 'gohenry',
      section: `category.${category.slug}`,
      published: '',
      modified: '',
      totalPages: [this.currentPage, this.totalPages, this.altTotalPages]
    };

    const jsonLdObject = this.jsonLdService.getObject('Website', {
      name: 'GoHenry | Making every kid good with money – blog',
      url: seoData.url,
      description: 'The GoHenry pocket money card is a smart solution for managing your child\'s money and giving money to children, through an allowance prepaid card.'
    });
    const jsonLdObject2 = this.jsonLdService.getObject('Organization', {
      url: environment.website,
      logo: environment.jsonImage,
      legalName: 'GoHenry',
      name: 'GoHenry'
    });

    this.socialShareService.setData(seoData);
    this.jsonLdService.setData([jsonLdObject, jsonLdObject2]);
    this.trackingService.setDataLayerAttributes({
      event: 'pageView',
      event_name: 'page_view',
      page: {
        location: this.location.path(),
        environment: environment.env,
        locale: environment.locale,
        title: seoData.title,
        feature: 'blog',
      }
    });
  }
}
